<template>
  <div class="flight-bookings">
    <app-cells position="between">
      <h1 class="title">Flight bookings</h1>
      <app-cells v-if="canCreateBookings" :indent="false" gap="10px 12px">
        <app-button theme="dark" @click.native="openImportModal">Import</app-button>
        <app-button @click.native="$router.push({ name: 'flights-aviasales' })">Search flights</app-button>
      </app-cells>
    </app-cells>

    <app-tabs :tabs="tabs" />
    <div class="flight-bookings__content">
      <app-cells gap="12px">
        <app-input v-model="search" class="flight-bookings__search" type="search" name="search" placeholder="Search by id, group name, PNR" aria-label="Search bookings" />
        <app-select
          v-model="airline"
          class="flight-bookings__airline"
          :options="airlines"
          :reduce="item => item.id"
          :loading="airlinesLoading"
          :disabled="airlinesLoading || Boolean(airlinesError)"
          label="name"
          placeholder="Airline"
          ariaLabel="Airline"
        />
        <app-filters v-model="bookingMode" :filters="bookingModes" name="flight-booking-mode" aria-label="Booking Mode" />
      </app-cells>
      <div v-if="airlinesError" class="flight-bookings__airlines-error" role="alert">
        <span>{{ airlinesError }}</span>
        <app-button theme="dark" size="small" @click.native="loadAirlines">Retry</app-button>
      </div>
      <router-view :filters="filters" />
    </div>

    <modal
      name="import-flight-booking"
      :width="480"
      height="auto"
      :adaptive="true"
      :clickToClose="false"
    >
      <div class="popup">
        <button @click="closeImportModal" class="popup__close" type="button"></button>
        <div class="popup__title">Import booking</div>
        <div class="popup__body">
          <form @submit.prevent="importBooking">
            <app-form-group>
              <div class="flight-bookings__import-type">
                <app-radio
                  v-model="importForm.type"
                  value="pnr"
                  name="flight-booking-import-type"
                  @change="clearImportError"
                >
                  <template #label>PNR</template>
                </app-radio>
                <app-radio
                  v-model="importForm.type"
                  value="confirmation"
                  name="flight-booking-import-type"
                  @change="clearImportError"
                >
                  <template #label>Confirmation</template>
                </app-radio>
              </div>
            </app-form-group>

            <template v-if="importForm.type === 'pnr'">
              <app-form-group label="PNR" required>
                <app-input
                  v-model="importForm.pnr"
                  :error="importSubmitted && !hasImportValue(importForm.pnr)"
                  name="pnr"
                  placeholder="PNR"
                />
                <template #error>
                  <div v-if="importSubmitted && !hasImportValue(importForm.pnr)">Required field</div>
                </template>
              </app-form-group>
              <app-form-group label="Last name" required>
                <app-input
                  v-model="importForm.lastName"
                  :error="importSubmitted && !hasImportValue(importForm.lastName)"
                  name="last_name"
                  placeholder="Last name"
                />
                <template #error>
                  <div v-if="importSubmitted && !hasImportValue(importForm.lastName)">Required field</div>
                </template>
              </app-form-group>
            </template>

            <app-form-group v-else label="Confirmation" required>
              <app-input
                v-model="importForm.confirmation"
                :error="importSubmitted && !hasImportValue(importForm.confirmation)"
                name="confirmation"
                placeholder="Confirmation"
              />
              <template #error>
                <div v-if="importSubmitted && !hasImportValue(importForm.confirmation)">Required field</div>
              </template>
            </app-form-group>

            <div v-if="importError === 'Booking not found'" class="flight-bookings__import-error">{{ importError }}</div>

            <app-cells position="center" :indent="false">
              <app-button ref="importSubmit" type="submit" :disabled="importLoading">Import</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { getFlightsAirlines, postFlightsBookingImport } from '@/http'
import AppTabs from '@/components/AppTabs'
import { responseError, rows } from './booking-detail'

export default {
  name: 'FlightBookingsLayout',
  components: { AppTabs },
  data() {
    return {
      tabs: [
        { name: 'flights-bookings', label: 'Active' },
        { name: 'flights-bookings-archive', label: 'Archive' },
        { name: 'flights-bookings-cancelled', label: 'Cancelled' }
      ],
      search: '',
      airline: null,
      bookingMode: '',
      bookingModes: [
        { value: '', label: 'All' },
        { value: 'manual', label: 'Manual' },
        { value: 'aerocrs', label: 'Auto' }
      ],
      airlines: [],
      airlinesLoading: true,
      airlinesError: '',
      importForm: {
        type: 'pnr',
        pnr: '',
        lastName: '',
        confirmation: ''
      },
      importSubmitted: false,
      importLoading: false,
      importError: ''
    }
  },
  created() {
    this.loadAirlines()
  },
  computed: {
    canCreateBookings() { return (this.$store.state.userGroups || []).some(group => group.code === 'general_manager') },
    filters() {
      return {
        ...(this.search && this.search.trim() ? { search: this.search.trim() } : {}),
        ...(this.airline ? { airline: this.airline } : {}),
        ...(this.bookingMode ? { booking_mode: this.bookingMode } : {})
      }
    },
    canImportBooking() {
      if (this.importForm.type === 'pnr') {
        return this.hasImportValue(this.importForm.pnr) && this.hasImportValue(this.importForm.lastName)
      }
      return this.hasImportValue(this.importForm.confirmation)
    }
  },
  methods: {
    async loadAirlines() {
      this.airlinesLoading = true
      this.airlinesError = ''
      try {
        const { data } = await getFlightsAirlines()
        this.airlines = rows(data)
      } catch (error) {
        this.airlinesError = responseError(error, 'Unable to load airlines')
      } finally {
        this.airlinesLoading = false
      }
    },
    openImportModal() {
      if (!this.canCreateBookings) return
      this.importForm = {
        type: 'pnr',
        pnr: '',
        lastName: '',
        confirmation: ''
      }
      this.importSubmitted = false
      this.importLoading = false
      this.importError = ''
      this.$modal.show('import-flight-booking')
    },
    closeImportModal() {
      if (this.importLoading) return
      this.$modal.hide('import-flight-booking')
    },
    clearImportError() {
      this.importSubmitted = false
      this.importError = ''
    },
    hasImportValue(value) {
      return Boolean(value && String(value).trim())
    },
    importPayload() {
      if (this.importForm.type === 'pnr') {
        return {
          pnr: this.importForm.pnr.trim(),
          last_name: this.importForm.lastName.trim()
        }
      }
      return { booking_confirmation: this.importForm.confirmation.trim() }
    },
    async importBooking() {
      this.importSubmitted = true
      this.importError = ''
      if (!this.canImportBooking) return

      this.importLoading = true
      this.$refs.importSubmit.preload = true
      try {
        const response = await postFlightsBookingImport(this.importPayload())
        const booking = response.data && response.data.booking ? response.data.booking : response.data

        if (!booking || typeof booking !== 'object' || !booking.id) {
          this.importError = 'Booking not found'
          return
        }

        this.$modal.hide('import-flight-booking')
        await this.$router.push({ name: 'flights-bookings-detail', params: { id: booking.id } })
      } catch (error) {
        const data = error.response && error.response.data
        const message = typeof data === 'string' ? data : data && (data.detail || data.error || data.message)
        if (typeof message === 'string' && message.toLowerCase() === 'not found') this.importError = 'Booking not found'
      } finally {
        this.importLoading = false
        if (this.$refs.importSubmit) this.$refs.importSubmit.preload = false
      }
    }
  }
}
</script>

<style lang="sass" scoped>
.flight-bookings
  min-width: 0
  padding-bottom: $ui-space-48

.flight-bookings__content
  max-width: 1000px
  min-width: 0

.flight-bookings__import-type
  display: flex
  align-items: center
  gap: 24px

.flight-bookings__import-error
  margin: -5px 0 20px
  color: $color-error
  font-size: 13px
  text-align: center

.flight-bookings__search
  flex: 1 1 280px
  width: auto
  min-width: 0

.flight-bookings__airline
  flex: 1 1 220px
  min-width: 0

.flight-bookings__airlines-error
  display: flex
  align-items: center
  flex-wrap: wrap
  gap: 12px
  margin-bottom: 20px
  color: $color-error
  font-size: 13px

</style>
